package info.rezident.app.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.TextDecorationLine
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import info.rezident.app.core.AppErrorMessages
import info.rezident.app.core.AppTracking
import info.rezident.app.data.DependencyInjection
import info.rezident.app.data.auth.AuthRepository
import info.rezident.app.pages.profile.ProfileState
import info.rezident.app.seo.setMetaNameTag
import info.rezident.app.ui.components.profile.AccountInformationCard
import info.rezident.app.ui.components.profile.OrientationQuizzesSection
import info.rezident.app.ui.components.profile.ProfileBannerCard
import info.rezident.app.ui.components.sections.NavHeader
import info.rezident.app.ui.components.widgets.Spacer
import info.rezident.app.ui.theme.AppTheme
import kotlinx.browser.document
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Progress

@Page("/profil")
@Composable
fun MyProfilePage() {
    LaunchedEffect(Unit) {
        document.title = "Profilul meu"
        document.setMetaNameTag(name = "robots", content = "noindex, nofollow")
    }

    val viewModel = DependencyInjection.profileViewModel
    val state by viewModel.state.collectAsState()
    val pageContext = rememberPageContext()

    // Check if user is signed in and not anonymous
    val isOnline = remember { true }

    // Redirect unauthenticated users to Auth page
    LaunchedEffect(Unit) {
        val firebaseUser = AuthRepository.getCurrentUser()
        if (firebaseUser == null || firebaseUser.isAnonymous) {
            pageContext.router.navigateTo("/conectare")
        }
    }

    LaunchedEffect(Unit) {
        viewModel.loadProfile()
    }

    Column(Modifier.fillMaxSize(), horizontalAlignment = Alignment.CenterHorizontally) {
        NavHeaderSpacer()
        NavHeader(isSimple = true)

        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(leftRight = 24.px, topBottom = 40.px)
                .maxWidth(1200.px),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(24.px)
        ) {
            when {
                state.isLoading -> LoadingProfileState()
                state.errorMessage != null ->
                    ErrorProfileState(
                        errorMessage = AppErrorMessages.GENERIC_ERROR_MESSAGE,
                        onRetry = { viewModel.retryLoadProfile() }
                    )

                else -> ProfileContent(
                    state = state,
                    isOnline = isOnline,
                    onQuizClick = { quizId -> viewModel.onQuizClicked(quizId, pageContext) },
                    onStartNewQuizClick = { viewModel.onStartNewQuizClicked(pageContext) }
                )
            }
        }
    }
}

@Composable
private fun NavHeaderSpacer() {
    Spacer(
        navHeaderHeightStyle
            .toModifier()
            .id("acasa")
    )
}

@Composable
private fun LoadingProfileState() {
    Column(
        horizontalAlignment = Alignment.CenterHorizontally,
        modifier = Modifier.fillMaxWidth().padding(64.px)
    ) {
        SpanText(
            text = "Profilul tău se încarcă...",
            modifier = Modifier.margin(bottom = 16.px).color(AppTheme.colors.textSecondary)
        )
        Progress {}
    }
}

@Composable
private fun ErrorProfileState(errorMessage: String, onRetry: () -> Unit) {
    Column(
        horizontalAlignment = Alignment.CenterHorizontally,
        modifier = Modifier.fillMaxWidth().padding(64.px)
    ) {
        SpanText(
            text = errorMessage,
            modifier = Modifier.color(AppTheme.colors.text).margin(bottom = 16.px)
                .textAlign(TextAlign.Center)
        )
        Button(onClick = { onRetry() }) {
            SpanText(AppErrorMessages.RELOAD_ACTION)
        }
    }
}

@Composable
private fun ProfileContent(
    state: ProfileState,
    isOnline: Boolean,
    onQuizClick: (String) -> Unit,
    onStartNewQuizClick: () -> Unit
) {
    Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(24.px),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        // Profile banner
        ProfileBannerCard(
            displayName = state.displayName,
            initials = state.initials,
            roleText = state.roleText,
            isOnline = isOnline
        )

        // Account information
        AccountInformationCard(
            email = state.email,
            memberSince = state.memberSince,
            academicYear = state.academicYear
        )

        // Orientation quizzes section
        if (state.quizzes.isNotEmpty()) {
            OrientationQuizzesSection(
                quizzes = state.quizzes,
                completedCount = state.completedCount,
                onQuizClick = onQuizClick,
                onStartNewQuizClick = onStartNewQuizClick
            )
        } else {
            EmptyQuizzesState(onStartNewQuizClick)
        }

        Spacer(Modifier.height(16.px))

        val context = rememberPageContext()
        val scope = rememberCoroutineScope()
        SpanText(
            "Ieși din cont",
            AppTheme.typography.body2.toModifier()
                .color(AppTheme.colors.textTertiary)
                .textDecorationLine(TextDecorationLine.Underline)
                .cursor(Cursor.Pointer)
                .onClick {
                    scope.launch {
                        // Analytics: user logs out
                        AppTracking.logEvent(AppTracking.EVENT_LOG_OUT)
                        AuthRepository.logOut()
                        context.router.navigateTo("/")
                    }
                }
        )
    }
}

@Composable
private fun EmptyQuizzesState(onStartNewQuizClick: () -> Unit) {
    Column(
        horizontalAlignment = Alignment.CenterHorizontally,
        modifier = Modifier.fillMaxWidth().padding(64.px)
    ) {
        SpanText(
            text = "Încă nu ai completat un Quiz de Orientare",
            modifier = Modifier.color(AppTheme.colors.text).margin(bottom = 16.px)
                .textAlign(TextAlign.Center)
                .then(AppTheme.typography.h2.toModifier())
        )
        SpanText(
            text = "Completează primul tău Quiz de Orientare pentru a afla ce specialități ți se potrivesc la Rezi!",
            modifier = Modifier.color(AppTheme.colors.textSecondary).margin(bottom = 24.px)
                .textAlign(TextAlign.Center)
                .then(AppTheme.typography.body1.toModifier())
        )

        CtaButton("Începe acum") {
            onStartNewQuizClick()
        }
    }
}